import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Overlays from "./components/Overlays";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FaqPage from "./pages/FaqPage";
import Blog, { BlogPost } from "./pages/Blog";
import AdminBlog from "./pages/AdminBlog";
import {
  MotorInsurance,
  HealthInsurance,
  LifeInsurance,
  BusinessInsurance,
  TravelInsurance,
} from "./pages/services";
import { PrivacyPolicy, TermsConditions, Disclaimer } from "./pages/Legal";
import Admin from "./pages/Admin";
import { applyPageMeta } from "./lib/seo";

const PAGES = {
  "/": {
    t: "Pocket Bima Insurance Service | Simple & Affordable Insurance in Greater Noida West",
    d: "Pocket Bima Insurance Service offers car, bike, health, life, term, travel and business insurance assistance in Greater Noida West. Get free quotes.",
  },
  "/about": {
    t: "About Us | Pocket Bima Insurance Service — Chhoti Pocket, Badi Suraksha",
    d: "Pocket Bima Insurance Service is a digital-first insurance assistance brand from Greater Noida West, founded by Gaurav Singh, making insurance simple for all.",
  },
  "/motor-insurance": {
    t: "Motor Insurance in Greater Noida West — Car, Bike, Taxi & GCV | Pocket Bima",
    d: "Car, bike, taxi and commercial vehicle insurance assistance in Greater Noida West. Understand third-party, comprehensive cover and add-ons with Pocket Bima.",
  },
  "/health-insurance": {
    t: "Health Insurance in Greater Noida West — Family & Group | Pocket Bima",
    d: "Individual, family and group health insurance guidance in Greater Noida West. Pocket Bima Insurance Service explains coverage and waiting periods simply.",
  },
  "/life-insurance": {
    t: "Term & Life Insurance in Greater Noida West | Pocket Bima Insurance Service",
    d: "Term and life insurance guidance in Greater Noida West. Plan your family's financial protection with simple, honest help from Pocket Bima Insurance Service.",
  },
  "/business-insurance": {
    t: "Business Insurance in Greater Noida West — Fire, Marine, WC | Pocket Bima",
    d: "Fire, marine, workmen compensation and single transit insurance for businesses in Greater Noida West and Orai. Protect your hard work with Pocket Bima.",
  },
  "/travel-insurance": {
    t: "Travel Insurance in Greater Noida West — Domestic & International | Pocket Bima",
    d: "Travel insurance assistance for domestic and international trips from Greater Noida West. Medical cover, baggage, delays — travel more, worry less.",
  },
  "/contact": {
    t: "Contact Us | Pocket Bima Insurance Service — Greater Noida West & Orai",
    d: "Call +91 95980 63001 or email Pocket Bima Insurance Service for insurance help. Offices in Greater Noida West and Orai, Uttar Pradesh. Free quotes.",
  },
  "/faq": {
    t: "Insurance FAQs — 75+ Questions Answered Simply | Pocket Bima Insurance Service",
    d: "Simple answers to 75+ insurance questions — motor, health, life, travel and business — plus how Pocket Bima Insurance Service works. No jargon, ever.",
  },
  "/blog": {
    t: "Insurance Blog — Simple Guides in Plain Language | Pocket Bima",
    d: "Easy insurance guides in simple language from Pocket Bima Insurance Service — motor, health, term, travel and business topics for Indian families.",
  },
  "/privacy-policy": {
    t: "Privacy Policy | Pocket Bima Insurance Service",
    d: "How Pocket Bima Insurance Service collects, uses and protects the information you share with us. Your privacy, explained simply.",
  },
  "/terms-and-conditions": {
    t: "Terms & Conditions | Pocket Bima Insurance Service",
    d: "Terms of use for the Pocket Bima Insurance Service website and insurance assistance services, Greater Noida West and Orai, Uttar Pradesh.",
  },
  "/disclaimer": {
    t: "Disclaimer | Pocket Bima Insurance Service",
    d: "Pocket Bima Insurance Service provides insurance solutions and assistance. Policies are subject to the insurer's terms and applicable regulations.",
  },
};

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const p = PAGES[pathname] || PAGES["/"];
    applyPageMeta(p.t, p.d, pathname);
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);

  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/motor-insurance" element={<MotorInsurance />} />
            <Route path="/health-insurance" element={<HealthInsurance />} />
            <Route path="/life-insurance" element={<LifeInsurance />} />
            <Route path="/business-insurance" element={<BusinessInsurance />} />
            <Route path="/travel-insurance" element={<TravelInsurance />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </div>
        <Footer />
        <Overlays />
        <Toaster position="top-center" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
